import io from 'socket.io-client';
var externalSocket = null;
var pingInterval = null;
var reconnectTimeout = null;
var isConnecting = false;
var connectionAttempts = 0;
var MAX_RECONNECT_DELAY = 30000; // 30 seconds max delay
var getToken = function () {
    return localStorage.getItem('token');
};
var getRestaurantId = function () {
    var stored = localStorage.getItem('selectedRestaurantId') || localStorage.getItem('restaurantId');
    return stored ? Number(stored) : null;
};
var createExternalSocket = function () {
    var token = getToken();
    var restaurantId = getRestaurantId();
    if (!token || !restaurantId) {
        console.warn('[External Socket] Token or restaurantId not found, cannot connect');
        throw new Error('Token or restaurantId is missing');
    }
    var options = {
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        transports: ['websocket', 'polling'],
        query: {
            token: token,
            restaurantId: restaurantId.toString(),
        },
        forceNew: true, // Force new connection
    };
    var socket = io('https://service.ego.ru.net/external', options);
    // For local development: const socket = io('http://localhost:3000/external', options);
    // Connection event handlers
    socket.on('connect', function () {
        console.log('[External Socket] Connected successfully');
        connectionAttempts = 0;
        isConnecting = false;
        // Start keep-alive ping
        startKeepAlive(socket);
    });
    socket.on('disconnect', function (reason) {
        console.warn('[External Socket] Disconnected:', reason);
        stopKeepAlive();
        // If it's not a manual disconnect, try to reconnect
        if (reason === 'io server disconnect') {
            // Server disconnected, need to reconnect manually
            socket.connect();
        }
        else if (reason === 'io client disconnect') {
            // Client disconnected manually, don't reconnect
            return;
        }
        else {
            // Network error or other, will auto-reconnect
            scheduleReconnect();
        }
    });
    socket.on('connect_error', function (error) {
        console.error('[External Socket] Connection error:', error);
        isConnecting = false;
        connectionAttempts++;
        // Exponential backoff for reconnection
        var delay = Math.min(1000 * Math.pow(2, connectionAttempts), MAX_RECONNECT_DELAY);
        scheduleReconnect(delay);
    });
    socket.on('reconnect', function (attemptNumber) {
        console.log("[External Socket] Reconnected after ".concat(attemptNumber, " attempts"));
        connectionAttempts = 0;
        isConnecting = false;
        startKeepAlive(socket);
    });
    socket.on('reconnect_attempt', function (attemptNumber) {
        console.log("[External Socket] Reconnection attempt ".concat(attemptNumber));
    });
    socket.on('reconnect_error', function (error) {
        console.error('[External Socket] Reconnection error:', error);
    });
    socket.on('reconnect_failed', function () {
        console.error('[External Socket] Reconnection failed');
        isConnecting = false;
    });
    return socket;
};
var startKeepAlive = function (socket) {
    // Clear any existing interval
    stopKeepAlive();
    // Send ping every 10 seconds to keep connection alive
    // Backend expects 'externalPing' event
    pingInterval = setInterval(function () {
        if (socket.connected) {
            try {
                socket.emit('externalPing');
                console.log('[External Socket] Keep-alive ping sent');
            }
            catch (error) {
                console.error('[External Socket] Error sending keep-alive ping:', error);
            }
        }
        else {
            console.warn('[External Socket] Socket not connected, skipping keep-alive ping');
            stopKeepAlive();
        }
    }, 10000); // Every 10 seconds
};
var stopKeepAlive = function () {
    if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
    }
};
var scheduleReconnect = function (delay) {
    if (delay === void 0) {
        delay = 2000;
    }
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
    }
    reconnectTimeout = setTimeout(function () {
        if (!externalSocket || !externalSocket.connected) {
            console.log('[External Socket] Attempting to reconnect...');
            reconnect();
        }
    }, delay);
};
var reconnect = function () {
    if (isConnecting) {
        return;
    }
    try {
        disconnectExternalSocket();
        isConnecting = true;
        externalSocket = createExternalSocket();
    }
    catch (error) {
        console.error('[External Socket] Error during reconnection:', error);
        isConnecting = false;
    }
};
export var getExternalSocket = function () {
    if (!externalSocket) {
        externalSocket = createExternalSocket();
    }
    else if (!externalSocket.connected && !isConnecting) {
        // If socket exists but not connected, try to reconnect
        console.log('[External Socket] Socket exists but not connected, reconnecting...');
        reconnect();
    }
    return externalSocket;
};
export var disconnectExternalSocket = function () {
    stopKeepAlive();
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }
    if (externalSocket) {
        externalSocket.disconnect();
        externalSocket = null;
    }
    isConnecting = false;
    connectionAttempts = 0;
};
export var isExternalSocketConnected = function () {
    return (externalSocket === null || externalSocket === void 0 ? void 0 : externalSocket.connected) || false;
};
// Reconnect when token or restaurantId changes
export var reconnectExternalSocket = function () {
    console.log('[External Socket] Forcing reconnection...');
    disconnectExternalSocket();
    externalSocket = createExternalSocket();
};
// Safely emit an event with retry logic
export var safeEmit = function (event, data, retries) {
    if (retries === void 0) {
        retries = 3;
    }
    return new Promise(function (resolve, reject) {
        if (!externalSocket) {
            try {
                externalSocket = createExternalSocket();
            }
            catch (error) {
                reject(new Error('Cannot create external socket'));
                return;
            }
        }
        var attemptEmit = function (attempt) {
            if (externalSocket === null || externalSocket === void 0 ? void 0 : externalSocket.connected) {
                try {
                    externalSocket.emit(event, data);
                    resolve();
                }
                catch (error) {
                    if (attempt < retries) {
                        console.warn("[External Socket] Emit failed, retrying... (".concat(attempt + 1, "/").concat(retries, ")"));
                        setTimeout(function () { return attemptEmit(attempt + 1); }, 1000 * attempt);
                    }
                    else {
                        reject(error);
                    }
                }
            }
            else {
                if (attempt < retries) {
                    console.warn("[External Socket] Socket not connected, waiting... (".concat(attempt + 1, "/").concat(retries, ")"));
                    // Wait for connection
                    var checkConnection_1 = setInterval(function () {
                        if (externalSocket === null || externalSocket === void 0 ? void 0 : externalSocket.connected) {
                            clearInterval(checkConnection_1);
                            attemptEmit(attempt);
                        }
                    }, 500);
                    setTimeout(function () {
                        clearInterval(checkConnection_1);
                        if (attempt < retries) {
                            attemptEmit(attempt + 1);
                        }
                        else {
                            reject(new Error('Socket not connected after retries'));
                        }
                    }, 3000);
                }
                else {
                    reject(new Error('Socket not connected'));
                }
            }
        };
        attemptEmit(1);
    });
};
export default getExternalSocket;
